import React, { useEffect, useMemo, useState } from 'react';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';

import { useAvailableViews } from 'domain/surveyDataManager/hooks';
import {
  AvailableViewSummary,
  resolveAvailableViewGroups,
} from 'domain/surveyDataManager/contracts/availableViews';
import { mergeDefectOverlayViews } from 'domain/surveyDataManager/defectOverlays/mergeDefectOverlayViews';
import { RecipeComponentFailure } from 'domain/surveyDataManager/contracts/recipes';
import { filterEntitledViews } from 'domain/surveyDataManager/entitlements/stubEntitlements';
import {
  ActiveDefectLayer,
  ActiveDefectLayerId,
  selectLayerActivation,
} from 'state/surveyDataManager';
import { DefectCatalogItem } from 'domain/surveyDataManager/contracts';
import type { ActiveLayerVisualizationSettings } from 'domain/surveyDataManager/visualization/activeLayerVisualizationSettings';
import {
  FAVOURITES_CHANGED_EVENT,
  type FavouriteLayerNode,
  type FavouritesChangedDetail,
  dispatchFavouritesChanged,
  getFavouriteBaseKeys,
  loadFavouritesMap,
  saveFavouritesMap,
  toggleFavourite,
} from 'utils/favouritesStorage';
import { getTenantIdFromStorage } from 'utils/tenant';

import ActiveLayersPanel from '../ActiveLayersPanel';
import InlineErrorBanner from '../InlineErrorBanner';
import AvailableViewGroup from './AvailableViewGroup';
import {
  filterAvailableViewsBySearch,
  filterPersonalLayersBySearch,
} from './filterAvailableViews';
import { buildSdmPreconfiguredFavouriteKey } from './sdmFavouriteKeys';
import { collectActiveRecipeIds } from '../../recipeVisibility';

import './AvailableViewsPanel.scss';

const SDM_FAVOURITES_PARENT_NAME = 'Survey Data Viewer';

function buildPreconfiguredFavouriteNode(
  view: AvailableViewSummary,
): FavouriteLayerNode {
  return {
    key: buildSdmPreconfiguredFavouriteKey(view.id),
    title: view.name,
    category: SDM_FAVOURITES_PARENT_NAME,
    type: 'sdm-preconfigured-view',
    isLeaf: true,
  };
}

export interface AvailableViewsPanelProps {
  readonly refreshData?: boolean;
  readonly layers: readonly ActiveDefectLayer[];
  readonly defects: readonly DefectCatalogItem[];
  readonly expandedLayerId: ActiveDefectLayerId | null;
  readonly advancedSettingsLayerId: ActiveDefectLayerId | null;
  readonly isAddLayerOpen?: boolean;
  readonly isAddLayerLoading?: boolean;
  readonly isAddingLayer?: boolean;
  readonly isCatalogueLoading?: boolean;
  onAddLayer: () => void;
  onActivateRecipe: (
    view: AvailableViewSummary,
  ) => Promise<readonly RecipeComponentFailure[]>;
  onDeactivateRecipe: (recipeId: string) => void;
  onToggleVisibility: (layerId: ActiveDefectLayerId) => void;
  onRemove: (layerId: ActiveDefectLayerId) => void;
  onToggleSettings: (layerId: ActiveDefectLayerId) => void;
  onToggleAdvancedSettings: (layerId: ActiveDefectLayerId) => void;
  onDisplayNameChange: (layerId: ActiveDefectLayerId, displayName: string) => void;
  onOpacityChange: (layerId: ActiveDefectLayerId, opacity: number) => void;
  onHighlightToggle: (layerId: ActiveDefectLayerId) => void;
  onLineWidthChange: (layerId: ActiveDefectLayerId, lineWidth: number) => void;
  onLineStyleChange: (layerId: ActiveDefectLayerId, lineStyle: string) => void;
  onVisualizationSettingsSave: (
    layerId: ActiveDefectLayerId,
    settings: ActiveLayerVisualizationSettings,
  ) => void;
}

function groupViews(
  views: readonly AvailableViewSummary[],
  groupId: AvailableViewSummary['groupId'],
): readonly AvailableViewSummary[] {
  return views.filter((view) => view.groupId === groupId);
}

function AvailableViewsPanel({
  refreshData = false,
  layers,
  defects,
  expandedLayerId,
  advancedSettingsLayerId,
  isAddLayerOpen = false,
  isAddLayerLoading = false,
  isAddingLayer = false,
  isCatalogueLoading = false,
  onAddLayer,
  onActivateRecipe,
  onDeactivateRecipe,
  onToggleVisibility,
  onRemove,
  onToggleSettings,
  onToggleAdvancedSettings,
  onDisplayNameChange,
  onOpacityChange,
  onHighlightToggle,
  onLineWidthChange,
  onLineStyleChange,
  onVisualizationSettingsSave,
}: AvailableViewsPanelProps): JSX.Element {
  const availableViewsQuery = useAvailableViews(refreshData);
  const views = filterEntitledViews(
    mergeDefectOverlayViews({
      views: availableViewsQuery.data?.views ?? [],
    }).views,
  );

  const [layerSearch, setLayerSearch] = useState('');
  const [expandedViewId, setExpandedViewId] = useState<string | null>(null);
  const [activatingViewIds, setActivatingViewIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [activationFailuresByViewId, setActivationFailuresByViewId] = useState<
    Readonly<Record<string, readonly RecipeComponentFailure[]>>
  >({});
  const [favouriteViewKeys, setFavouriteViewKeys] = useState<Set<string>>(
    () => getFavouriteBaseKeys(loadFavouritesMap()),
  );
  const layerActivation = useSelector(selectLayerActivation);

  const loadingViewIds = useMemo((): ReadonlySet<string> => {
    const ids = new Set<string>(activatingViewIds);
    if (
      layerActivation.status === 'pending' &&
      layerActivation.kind === 'recipe' &&
      layerActivation.recipeId
    ) {
      ids.add(layerActivation.recipeId);
    }
    return ids;
  }, [activatingViewIds, layerActivation]);

  useEffect(() => {
    const onFavouritesChanged = (event: Event) => {
      const detail = (event as CustomEvent<FavouritesChangedDetail>).detail;
      if (!detail) {
        return;
      }
      setFavouriteViewKeys(getFavouriteBaseKeys(new Map(detail.entries)));
    };
    window.addEventListener(FAVOURITES_CHANGED_EVENT, onFavouritesChanged);
    return () =>
      window.removeEventListener(FAVOURITES_CHANGED_EVENT, onFavouritesChanged);
  }, []);

  const personalLayers = layers.filter((layer) => layer.recipeId == null);
  const visibleViewIds = collectActiveRecipeIds(layers, views, defects);

  const filteredViews = filterAvailableViewsBySearch(views, layerSearch);
  const filteredPersonalLayers = filterPersonalLayersBySearch(
    personalLayers,
    layerSearch,
  );
  const hasLayerSearch = layerSearch.trim().length > 0;

  const ptsViews = groupViews(filteredViews, 'ptsStandard');
  const clientViews = groupViews(filteredViews, 'clientStandard');

  const viewGroups = resolveAvailableViewGroups(getTenantIdFromStorage());
  const ptsMeta = viewGroups.find((group) => group.id === 'ptsStandard');
  const clientMeta = viewGroups.find((group) => group.id === 'clientStandard');
  const myLayersMeta = viewGroups.find((group) => group.id === 'myLayers');
  const showPtsGroup = Boolean(ptsMeta) && (!hasLayerSearch || ptsViews.length > 0);
  const showClientGroup =
    Boolean(clientMeta) && (!hasLayerSearch || clientViews.length > 0);
  const showMyLayersGroup =
    Boolean(myLayersMeta) &&
    (!hasLayerSearch || filteredPersonalLayers.length > 0);
  const hasNoSearchMatches =
    hasLayerSearch && !showPtsGroup && !showClientGroup && !showMyLayersGroup;

  const handleToggleViewVisibility = (viewId: string): void => {
    if (isCatalogueLoading) {
      return;
    }

    const view = views.find((entry) => entry.id === viewId);
    if (!view) {
      return;
    }

    if (visibleViewIds.has(viewId)) {
      onDeactivateRecipe(viewId);
      setActivatingViewIds((current) => {
        if (!current.has(viewId)) {
          return current;
        }
        const next = new Set(current);
        next.delete(viewId);
        return next;
      });
      setActivationFailuresByViewId((current) => {
        const next = { ...current };
        delete next[viewId];
        return next;
      });
      return;
    }

    if (activatingViewIds.has(viewId)) {
      return;
    }

    setActivatingViewIds((current) => {
      const next = new Set(current);
      next.add(viewId);
      return next;
    });
    void Promise.resolve(onActivateRecipe(view))
      .then((failures) => {
        setActivationFailuresByViewId((current) => ({
          ...current,
          [viewId]: failures,
        }));
        if (failures.length > 0) {
          setExpandedViewId(viewId);
        }
      })
      .catch(() => {
        setExpandedViewId(viewId);
      })
      .finally(() => {
        setActivatingViewIds((current) => {
          const next = new Set(current);
          next.delete(viewId);
          return next;
        });
      });
  };

  const handleToggleViewDetails = (viewId: string): void => {
    setExpandedViewId((current) => (current === viewId ? null : viewId));
  };

  const handleToggleFavourite = (view: AvailableViewSummary): void => {
    const current = loadFavouritesMap();
    const { favourites: nextFavourites, isAdding } = toggleFavourite(
      current,
      buildPreconfiguredFavouriteNode(view),
      SDM_FAVOURITES_PARENT_NAME,
    );
    saveFavouritesMap(nextFavourites);
    setFavouriteViewKeys(getFavouriteBaseKeys(nextFavourites));
    dispatchFavouritesChanged(nextFavourites, isAdding);
  };

  return (
    <section className="sdm-available-views" aria-label="Layers and recipes">
      <header className="sdm-available-views__header">
        <div>
          <h2 className="sdm-available-views__title">Layers & Recipes</h2>
          {/* <p className="sdm-available-views__subtitle">
            One-click built-in and client recipes for the map.
          </p> */}
        </div>
        <div className="sdm-available-views__search">
          <label
            className="sdm-available-views__search-label"
            htmlFor="sdm-available-views-search"
          >
            Search layers
          </label>
          <input
            id="sdm-available-views-search"
            type="search"
            className="sdm-available-views__search-input"
            value={layerSearch}
            onChange={(event) => setLayerSearch(event.target.value)}
            placeholder="Search layers and recipes…"
            aria-label="Search layers and recipes"
          />
          {layerSearch.length > 0 ? (
            <button
              type="button"
              className="sdm-available-views__search-clear"
              onClick={() => setLayerSearch('')}
              aria-label="Clear search"
            >
              <MdClose aria-hidden />
            </button>
          ) : null}
        </div>
        {/* <button
          type="button"
          className="sdm-available-views__add"
          onClick={onAddLayer}
          aria-expanded={isAddLayerOpen}
          aria-busy={isAddLayerLoading || isAddingLayer || undefined}
          disabled={isAddingLayer}
        >
          {isAddingLayer
            ? 'Adding layer…'
            : isAddLayerOpen
              ? 'Back to list'
              : 'Add layer'}
        </button> */}
      </header>

      {availableViewsQuery.isError ? (
        <InlineErrorBanner
          title="Couldn't load available views"
          error={availableViewsQuery.error}
          onRetry={() => availableViewsQuery.refetch()}
        />
      ) : null}

      {availableViewsQuery.isLoading && !availableViewsQuery.data ? (
        <p className="sdm-available-views__loading" role="status">
          Loading available views…
        </p>
      ) : null}

      {hasNoSearchMatches ? (
        <p className="sdm-available-views__search-empty" role="status">
          No layers match “{layerSearch.trim()}”.
        </p>
      ) : null}

      {showPtsGroup && ptsMeta ? (
        <AvailableViewGroup
          title={ptsMeta.title}
          description={ptsMeta.description}
          emptyTitle={ptsMeta.emptyTitle}
          emptyBody={ptsMeta.emptyBody}
          views={ptsViews}
          itemCount={ptsViews.length}
          preferExpanded={hasLayerSearch}
          visibleViewIds={visibleViewIds}
          expandedViewId={expandedViewId}
          loadingViewIds={loadingViewIds}
          isCatalogueLoading={isCatalogueLoading}
          activationFailuresByViewId={activationFailuresByViewId}
          favouriteViewKeys={favouriteViewKeys}
          onToggleVisibility={handleToggleViewVisibility}
          onToggleDetails={handleToggleViewDetails}
          onToggleFavourite={handleToggleFavourite}
        />
      ) : null}

      {showClientGroup && clientMeta ? (
        <AvailableViewGroup
          title={clientMeta.title}
          description={clientMeta.description}
          emptyTitle={clientMeta.emptyTitle}
          emptyBody={clientMeta.emptyBody}
          views={clientViews}
          itemCount={clientViews.length}
          preferExpanded={hasLayerSearch}
          visibleViewIds={visibleViewIds}
          expandedViewId={expandedViewId}
          loadingViewIds={loadingViewIds}
          isCatalogueLoading={isCatalogueLoading}
          activationFailuresByViewId={activationFailuresByViewId}
          favouriteViewKeys={favouriteViewKeys}
          onToggleVisibility={handleToggleViewVisibility}
          onToggleDetails={handleToggleViewDetails}
          onToggleFavourite={handleToggleFavourite}
        />
      ) : null}

      {showMyLayersGroup && myLayersMeta ? (
        <AvailableViewGroup
          title={myLayersMeta.title}
          description={myLayersMeta.description}
          emptyTitle={myLayersMeta.emptyTitle}
          emptyBody={myLayersMeta.emptyBody}
          views={[]}
          itemCount={filteredPersonalLayers.length}
          preferExpanded={hasLayerSearch}
          visibleViewIds={visibleViewIds}
          expandedViewId={expandedViewId}
          loadingViewIds={loadingViewIds}
          isCatalogueLoading={isCatalogueLoading}
          activationFailuresByViewId={activationFailuresByViewId}
          favouriteViewKeys={favouriteViewKeys}
          onToggleVisibility={handleToggleViewVisibility}
          onToggleDetails={handleToggleViewDetails}
          onToggleFavourite={handleToggleFavourite}
        >
          <ActiveLayersPanel
            embedded
            layers={filteredPersonalLayers}
            defects={defects}
            expandedLayerId={expandedLayerId}
            advancedSettingsLayerId={advancedSettingsLayerId}
            isAddLayerOpen={isAddLayerOpen}
            isAddLayerLoading={isAddLayerLoading}
            isAddingLayer={isAddingLayer}
            onAddLayer={onAddLayer}
            onToggleVisibility={onToggleVisibility}
            onRemove={onRemove}
            onToggleSettings={onToggleSettings}
            onToggleAdvancedSettings={onToggleAdvancedSettings}
            onDisplayNameChange={onDisplayNameChange}
            onOpacityChange={onOpacityChange}
            onHighlightToggle={onHighlightToggle}
            onLineWidthChange={onLineWidthChange}
            onLineStyleChange={onLineStyleChange}
            onVisualizationSettingsSave={onVisualizationSettingsSave}
          />
        </AvailableViewGroup>
      ) : null}
    </section>
  );
}

export default AvailableViewsPanel;
